import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchAudienceByCity,
  clearAudienceByCity,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';
import Pagination from 'components/common/Pagination';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { cities as citiesFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';
// import CountryFlag from 'components/common/CountryFlag';

import './AnalyticsAudienceByCity.css';

const AnalyticsAudienceByCity = ({
  date,
  audienceByCity,
  audienceByCityError,
  fetchAudienceByCity,
  clearAudienceByCity,
  me,
}) => {
  const { t } = useTranslation();
  const itemsPerPage = 7; // define how many items must be on page
  const [itemsToDisplay, setItemsToDisplay] = useState({
    start: 1,
    end: itemsPerPage,
  });

  useEffect(() => {
    fetchAudienceByCity(date);

    return () => {
      clearAudienceByCity();
      setItemsToDisplay({ start: 1, end: itemsPerPage });
    };
  }, [date]);

  if (audienceByCityError) {
    return null;
  }

  if (audienceByCity && Object.keys(audienceByCity).length === 0 & me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Views by City")} text={t("Looks like you have no data to display yet…")} />;
  }

  const handlePaginationPageClick = (date) => {
    setItemsToDisplay({
      start: date.selected * itemsPerPage + 1,
      end: date.selected * itemsPerPage + itemsPerPage,
    });
  };

  const renderTableData = (audienceByCityData) => {
    if (!audienceByCityData) {
      return null;
    }

    const countPages = Math.ceil(Object.keys(audienceByCityData).length / itemsPerPage);
    return (
      <>
        <table>
          <thead>
            <tr>
              <th />
              <th />
              <th>{t("Views")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(audienceByCityData).slice(itemsToDisplay.start - 1, itemsToDisplay.end).map((i, index) => (
              <tr
                key={`analytics_audience_by_city_table_row_key_${index}`}
              >
                <td>{index + 1}.</td>
                <td>
                  {/* <CountryFlag name={i.toLowerCase()} />&nbsp;&nbsp; */}
                  <span>{i}</span>
                </td>
                <td className="views">
                  {audienceByCityData[i].views &&
                    <span className="value">{audienceByCityData[i].views}&nbsp;&nbsp;</span>
                  }
                  {audienceByCityData[i].percent &&
                    <span className="value-percent">({audienceByCityData[i].percent}&#37;)</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {Object.keys(audienceByCityData).length > itemsPerPage &&
          <Pagination
            containerClassName={'pagination-wrapper'}
            activeClassName={'active'}
            pageCount={countPages}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePaginationPageClick}
          />
        }
      </>
    );
  };

  return (
    <AnalyticsSection
      className="analytics-audience-by-city__component"
      header={
        <div className="title">
          <h3>{t("Views by City")}</h3>
        </div>
      }
    >
      <div className="analytics-audience-by-city-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderTableData(citiesFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderTableData(audienceByCity)}
          </>
        }
        <Loader active={!audienceByCity} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsAudienceByCity.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchAudienceByCity: PropTypes.func.isRequired,
  clearAudienceByCity: PropTypes.func.isRequired,
  audienceByCity: PropTypes.oneOfType([PropTypes.object]),
  audienceByCityError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsAudienceByCity.defaultProps = {
  audienceByCity: null,
  audienceByCityError: null,
};

const mapStateToProps = state => ({
  audienceByCity: state.analyticsReducer.audienceByCity,
  audienceByCityError: state.analyticsReducer.audienceByCityError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchAudienceByCity,
    clearAudienceByCity,
  },
)(AnalyticsAudienceByCity);
